import React, { useState } from 'react'
import { Menu, MenuItem } from '@mui/material'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import moment from 'moment-timezone'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'

import { Comment } from '../../types/commentTypes'
import { useAppDispatch, AppDispatch } from '../../redux/store'
import {
  getCommentsByActionId,
  updateComment,
} from '../../redux/comments/commentActions'
import { hasRole } from '../../utils/checkRole'
import { UserRole } from '../../types/keycloak'
import { selectUsername } from '../../redux/keycloak/keycloakSelectors'
import { DetailButton } from '../../components/common/detail'
import { DETAIL_COLORS } from '../../components/common/ListPageCells'
import '../../components/common/detail/detail.css'

interface CommentProps {
  comment: Comment
  onDelete: (_id: string) => void
}

const CommentaireC: React.FC<CommentProps> = ({ comment, onDelete }) => {
  const { t } = useTranslation()
  const dispatch: AppDispatch = useAppDispatch()
  const username = useSelector(selectUsername)
  const roles = useSelector((state: any) => state.auth.roles)

  const [isEditing, setEditing] = useState(false)
  const [editedContent, setEditedContent] = useState(comment.Commentaire)
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)

  const canManage =
    username?.toLocaleLowerCase() === comment?.CreatedBy?.toLocaleLowerCase() ||
    hasRole(roles, UserRole.ADMIN)

  const handleMenuClose = () => setMenuAnchorEl(null)

  const handleDelete = () => {
    if (comment?._id) {
      onDelete(comment._id)
      handleMenuClose()
    }
  }

  const handleEditClick = () => {
    setEditing(true)
    handleMenuClose()
  }

  const handleSaveEdit = async () => {
    if (comment?._id && comment.ActionId) {
      await dispatch(
        updateComment({
          updatedComment: { ...comment, Commentaire: editedContent },
          _id: comment._id,
        })
      )
      await dispatch(getCommentsByActionId(comment.ActionId))
      setEditing(false)
    }
  }

  const handleCancelEdit = () => {
    setEditedContent(comment.Commentaire)
    setEditing(false)
  }

  const timeAgo = moment(comment?.DateHeure).fromNow()
  const initial = (comment?.CreatedBy || '?').charAt(0).toUpperCase()

  return (
    <article className="detail-comment-card">
      <div className="detail-comment-avatar" aria-hidden>
        {initial}
      </div>
      <div className="detail-comment-body">
        <div className="detail-comment-top">
          <div>
            <p className="detail-comment-author">{comment.CreatedBy}</p>
            <p className="detail-comment-time">{timeAgo}</p>
          </div>
          {canManage ? (
            <button
              type="button"
              className="detail-icon-btn"
              aria-label="Comment actions"
              onClick={(e) => setMenuAnchorEl(e.currentTarget)}
            >
              <MoreVertIcon sx={{ fontSize: 18 }} />
            </button>
          ) : null}
        </div>

        {isEditing ? (
          <>
            <textarea
              className="detail-comment-textarea"
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              rows={3}
            />
            <div className="detail-comment-edit-actions">
              <DetailButton onClick={handleSaveEdit}>
                {t('taskDetailsPage.comment.btns.save')}
              </DetailButton>
              <DetailButton variant="ghost" onClick={handleCancelEdit}>
                {t('taskDetailsPage.comment.btns.cancel')}
              </DetailButton>
            </div>
          </>
        ) : (
          <p className="detail-comment-text">{comment.Commentaire}</p>
        )}
      </div>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        {username?.toLocaleLowerCase() ===
          comment?.CreatedBy?.toLocaleLowerCase() && (
          <MenuItem onClick={handleEditClick}>
            <EditIcon
              style={{ marginRight: 8, color: DETAIL_COLORS.tealDeep }}
              fontSize="small"
            />
            {t('taskDetailsPage.comment.menu.edit')}
          </MenuItem>
        )}
        {canManage && (
          <MenuItem onClick={handleDelete}>
            <DeleteIcon
              style={{ marginRight: 8, color: DETAIL_COLORS.coral }}
              fontSize="small"
            />
            {t('taskDetailsPage.comment.menu.delete')}
          </MenuItem>
        )}
      </Menu>
    </article>
  )
}

interface CommentFormProps {
  onSubmit: (Commentaire: string) => void
}

const CommentForm: React.FC<CommentFormProps> = ({ onSubmit }) => {
  const { t } = useTranslation()
  const [Commentaire, setCommentaire] = useState('')

  const handleSubmit = () => {
    if (Commentaire.trim() !== '') {
      onSubmit(Commentaire)
      setCommentaire('')
    }
  }

  return (
    <div className="detail-comment-composer">
      <div className="detail-comment-composer-head">
        <ChatBubbleOutlineRoundedIcon
          sx={{ fontSize: 20, color: DETAIL_COLORS.teal }}
        />
        <div>
          <p className="detail-comment-composer-title">
            {t('buttons.addComment')}
          </p>
          <p className="detail-comment-composer-hint">
            {t('taskDetailsPage.comment.input')}
          </p>
        </div>
      </div>
      <textarea
        className="detail-comment-textarea"
        placeholder={t('taskDetailsPage.comment.input')}
        value={Commentaire}
        onChange={(e) => setCommentaire(e.target.value)}
        rows={3}
      />
      <div className="detail-comment-composer-actions">
        <DetailButton
          variant="ghost"
          onClick={() => setCommentaire('')}
          disabled={!Commentaire}
        >
          {t('taskDetailsPage.comment.btns.cancel')}
        </DetailButton>
        <DetailButton onClick={handleSubmit} disabled={!Commentaire.trim()}>
          {t('taskDetailsPage.comment.btns.add')}
        </DetailButton>
      </div>
    </div>
  )
}

export { CommentaireC, CommentForm }
